<template>
  <b-modal
    :id="modalId"
    centered
    title="Submit Feedback Summary"
    @hide="$emit('close')"
    hide-header-close
    @show="resetModal"
    @hidden="onHidden"
    @ok.prevent="handleOk"
    size="lg"
  >
    <div class="p-2 d-flex flex-column gap-4">
      <!-- Course Info -->
      <div
        class="rounded px-4 py-2 d-flex flex-row justify-content-between align-items-center"
        style="background-color: #fafafa"
      >
        <div class="d-flex flex-column gap-1 align-items-center">
          <span>Date</span>
          <span>{{ data?.date }}</span>
        </div>
        <div class="d-flex flex-column gap-1 align-items-center">
          <span>Start Time</span>
          <span>{{ formatTimeToHHMM(data?.start_time) }}</span>
        </div>
        <div class="d-flex flex-column gap-1 align-items-center">
          <span>End Time</span>
          <span>{{ formatTimeToHHMM(data?.end_time) }}</span>
        </div>
      </div>

      <b-form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group label="Summary" description="Read Only">
          <b-form-textarea rows="2" v-model="form.summary" placeholder="Summary" disabled />
        </b-form-group>

        <b-form-group label="Follow Up" description="Read Only">
          <b-form-textarea rows="2" v-model="form.follow_up" placeholder="Follow Up" disabled />
        </b-form-group>

        <b-form-group label="Attachment" description="Read Only">
          <a :href="form.attachments" target="_blank" class="dropzone p-4 border rounded text-center">
            <div>
              <img
                v-if="previewUrl && !isPdf"
                :src="previewUrl"
                alt="Certificate Preview"
                class="img-fluid rounded mb-3"
                style="max-height: 250px"
              />
              <div v-else-if="isPdf" class="pdf-preview mb-3">
                <div class="pdf-icon-container">
                  <i class="fe fe-file-text"></i>
                </div>
                <p class="pdf-label mb-0">PDF File</p>
              </div>
              <div class="d-flex align-items-center justify-content-center gap-2">
                <span class="text-success">
                  <i class="fe fe-check-circle me-1"></i>
                  File
                </span>
              </div>
              <p class="text-muted small mt-2 mb-0">Size: "N/A"</p>
            </div>
          </a>
        </b-form-group>

        <b-form-group label="Feedback" :state="getState('feedback')" invalid-feedback="Feedback is required.">
          <b-form-textarea rows="2" v-model="form.feedback" placeholder="Feedback" :state="getState('follow_up')" />
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer>
      <b-button variant="primary" @click="handleOk" :disabled="isLoading" :key="isLoading">
        <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
        <span>{{ isLoading ? "Updating..." : "Update" }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { formatTimeToHHMM, isPdfFile } from "../utils/utils";

export default {
  name: "UpdateSummaryModal",

  props: {
    modalId: { type: String, default: "update-summary-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: this.getInitialForm(),
      selectedFile: null,
      previewUrl: null,
      isPdf: false,
      isDragging: false,
      submitted: false,
      formErrors: {},
      suppressValidation: false,
    };
  },

  watch: {
    data: {
      deep: true,
      handler(val) {
        this.populateFromData(val);
      },
    },
  },

  methods: {
    formatTimeToHHMM,
    isPdfFile,

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.feedback) this.formErrors.feedback = "Feedback is required.";

      return Object.keys(this.formErrors).length === 0;
    },

    handleSubmit() {
      const isValid = this.validateAll();
      if (!isValid) return;

      const payload = {
        id: this.form.id,
        feedback: this.form.feedback,
      };

      this.$emit("update-summary", payload);
    },

    getInitialForm() {
      return {
        summary: null,
        follow_up: null,
        feedback: null,
        attachments: [],
        id: null,
      };
    },

    populateFromData(d) {
      this.form = {
        summary: d.coaching_summary.summary,
        follow_up: d.coaching_summary.follow_up,
        feedback: d.coaching_summary.feedback,
        attachments: d.coaching_summary.attachments,
        id: d.coaching_summary.id,
      };

      if (d.coaching_summary.attachments) this.previewUrl = d.coaching_summary.attachments;

      this.isPdf = isPdfFile(d.coaching_summary.attachments);

      this.formErrors = {};
    },

    async resetModal() {
      if (this.isLoading) return;

      this.suppressValidation = true;

      await this.$nextTick();

      if (this.data) {
        this.populateFromData(this.data);
        this.previewUrl = this.data.coaching_summary.attachments;
        this.selectedFile = null;
      } else {
        this.form = this.getInitialForm();
        this.formErrors = {};
        this.selectedFile = null;
        this.previewUrl = null;
      }

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },
  },

  beforeDestroy() {
    // Clean up object URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}
</style>
